/** Construction-time classification of terrain tiles near native cliff faces.
 * The ordinary LOD traversal reuses this bound; no terrain sampling per frame. */
import {plateauProfileSample,samplePlateauProfile,type PlanetSampler} from '@hyperlight/shared';

const profile=plateauProfileSample();
/** A completed patch proves whether the nearby canyon really exposes a steep
 * face. Cache this once on adoption; flat wash floors need no extra foot level. */
export function canyonFaceNeedsDetail(slopes:Float32Array,surface?:Float32Array):boolean {
  if(!surface)return false;
  for(let i=0;i<slopes.length;i++)if(surface[i*2]>.3&&slopes[i]<.72)return true;
  return false;
}
type CanyonSampler=Pick<PlanetSampler,'def'|'canyons'>;
/** A waterfall's lower patch can be far below a camera at its lip. Retain the
 * existing foot-level detail over that finite footprint, including the 60 m
 * height fade used by traversal. This only reads immutable route descriptors;
 * constructing a node never needs extra terrain samples. */
export function waterfallRefinementHeight(sampler:Pick<PlanetSampler,'def'|'rivers'>,dx:number,dy:number,dz:number,spanM:number):number {
  const falls=sampler.rivers?.()?.falls;
  if(!falls?.length)return 0;
  const R=sampler.def.radius,padding=spanM*1.5;
  let height=0;
  for(const fall of falls){
    // A lip-centred bound covers the whole source reach, including its banks.
    // Chord distance understates surface distance, keeping the test conservative.
    const reach=fall.end.s-fall.lip.s+Math.max(fall.half*2.6,fall.escarpmentId?60:0)+padding;
    const x=(dx-fall.lip.x)*R,y=(dy-fall.lip.y)*R,z=(dz-fall.lip.z)*R;
    if(x*x+y*y+z*z>reach*reach)continue;
    height=Math.max(height,Math.min(1000,Math.max(80,fall.dropM+60)));
  }
  return height;
}
/** Reuse shared immutable route/segment bounds. Most nodes reject whole distant
 * routes before examining any reaches; no heightfield probes are required. */
export function canyonRefinementHeight(sampler:CanyonSampler,dx:number,dy:number,dz:number,spanM:number):number {
  const R=sampler.def?.radius??0;
  const padding=spanM*1.5;let height=0;
  for(const route of sampler.canyons?.()??[]){
    const x=(dx-route.center[0])*R,y=(dy-route.center[1])*R,z=(dz-route.center[2])*R;
    if(x*x+y*y+z*z>(route.radiusM+padding)**2)continue;
    for(const s of route.segments){
      const x=(dx-s.center[0])*R,y=(dy-s.center[1])*R,z=(dz-s.center[2])*R;
      if(x*x+y*y+z*z>(s.radiusM+padding)**2)continue;
      height=Math.max(height,Math.min(1000,Math.max(220,s.depthM+80)));
    }
  }
  return height;
}
/** Finite feature bounds also catch narrow buttes between coarse grid centres.
 * Chord distance understates tangent distance, keeping this conservative. */
export function butteRefinementHeight(sampler:Pick<PlanetSampler,'def'|'buttes'>,dx:number,dy:number,dz:number,spanM:number):number {
  const features=sampler.buttes?.();if(!features?.length)return 0;
  const R=sampler.def.radius;let height=0;
  for(const f of features){
    const reach=f.radiusM+spanM*1.5;
    const x=(dx-f.center[0])*R,y=(dy-f.center[1])*R,z=(dz-f.center[2])*R;
    if(x*x+y*y+z*z>reach*reach)continue;
    height=Math.max(height,Math.min(1000,Math.max(220,f.heightM+80)));
  }
  return height;
}
export function plateauRefinementHeight(sampler:Pick<PlanetSampler,'def'|'regions'>,dx:number,dy:number,dz:number,spanM:number):number {
  const features=sampler.regions?.();
  if(!features?.length)return 0;
  const radius=sampler.def.radius;
  let height=0;
  for(const feature of features){
    if(feature.kind!=='plateau')continue;
    const facing=dx*feature.center[0]+dy*feature.center[1]+dz*feature.center[2];
    if(facing<=0)continue;
    const u=(dx*feature.along[0]+dy*feature.along[1]+dz*feature.along[2])*radius/facing;
    // Cover the tile's projected diagonal, bend slope and edge setbacks. A
    // narrow cliff must not vanish from classification between tile centres.
    const padding=spanM*(1.5+3*feature.widthM/feature.halfLengthM)+Math.min(90,feature.widthM*.12);
    if(Math.abs(u)>feature.halfLengthM+padding)continue;
    const v=(dx*feature.across[0]+dy*feature.across[1]+dz*feature.across[2])*radius/facing;
    if(v< -feature.widthM-padding||v>4*feature.widthM+padding)continue;
    const section=Math.max(-1,Math.min(1,u/feature.halfLengthM)),knot=(section+1)*3;
    const k=Math.min(5,Math.floor(knot)),t=knot-k,blend=t*t*(3-2*t);
    const bend=feature.bends[k]+(feature.bends[k+1]-feature.bends[k])*blend;
    samplePlateauProfile(feature,u,v-bend,profile);
    if(profile.faceDistanceM>padding)continue;
    // Bound the cap/floor separation, including its longitudinal grade. The
    // final 60 m fades back to ordinary distance; high flight gains no detail.
    const rise=Math.min(feature.reliefM*.44,feature.halfLengthM*.12)
      +Math.abs(feature.floorEndM-feature.floorStartM)*.5;
    height=Math.max(height,Math.min(1000,Math.max(220,rise+80)));
  }
  return height;
}
